import { mapState } from 'pinia'
import { defineAsyncComponent } from 'vue'

import NavigationPins from 'src/components/navigation/navigation_pins.vue'
import GestureService from '../../services/gesture_service/gesture_service'
import {
  countExtraNotifications,
  unseenNotifications,
} from '../../services/notification_utils/notification_utils'

import { useAnnouncementsStore } from 'src/stores/announcements.js'
import { useChatsStore } from 'src/stores/chats.js'
import { useFollowRequestsStore } from 'src/stores/follow_requests.js'
import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useNotificationsStore } from 'src/stores/notifications.js'
import { useSyncConfigStore } from 'src/stores/sync_config.js'
import { useUsersStore } from 'src/stores/users.js'

import { countRows } from 'src/services/notification_groups/notification_groups.js'

const MobileNav = {
  components: {
    SideDrawer: defineAsyncComponent(
      () => import('src/components/side_drawer/side_drawer.vue'),
    ),
    Notifications: defineAsyncComponent(
      () => import('src/components/notifications/notifications.vue'),
    ),
    NavigationPins,
    ConfirmModal: defineAsyncComponent(
      () => import('src/components/confirm_modal/confirm_modal.vue'),
    ),
  },
  data: () => ({
    notificationsCloseGesture: undefined,
    notificationsOpen: false,
    notificationsAtTop: true,
    showingConfirmLogout: false,
  }),
  created() {
    this.notificationsCloseGesture = GestureService.swipeGesture(
      GestureService.DIRECTION_RIGHT,
      () => this.closeMobileNotifications(true),
      50,
    )
  },
  computed: {
    currentUser() {
      return useUsersStore().currentUser
    },
    unseenNotifications() {
      return unseenNotifications(
        useMergedConfigStore().mergedConfig.notificationVisibility,
        useMergedConfigStore().mergedConfig.ignoreInactionableSeen,
      )
    },
    unseenNotificationsCount() {
      return (
        this.unseenCount +
        countExtraNotifications(
          useMergedConfigStore().mergedConfig,
          useChatsStore().unreadChatsCount,
          useAnnouncementsStore().unreadAnnouncementsCount,
          useFollowRequestsStore().followRequestsCount,
        )
      )
    },
    unseenCount() {
      return countRows(this.unseenNotifications)
    },
    unseenCountBadgeText() {
      return `${this.unseenCount ? this.unseenCount : ''}`
    },
    shouldConfirmLogout() {
      return useMergedConfigStore().mergedConfig.modalOnLogout
    },
    closingDrawerMarksAsSeen() {
      return useMergedConfigStore().mergedConfig.closingDrawerMarksAsSeen
    },
    ...mapState(useAnnouncementsStore, ['unreadAnnouncementsCount']),
    ...mapState(useSyncConfigStore, {
      chatsPinned: (store) =>
        store.prefsStorage.collections.pinnedNavItems.includes('chats'),
    }),
    ...mapState(useChatsStore, ['unreadChatsCount']),
  },
  methods: {
    toggleMobileSidebar() {
      this.$refs.sideDrawer.toggleDrawer()
    },
    openMobileNotifications() {
      this.notificationsOpen = true
    },
    closeMobileNotifications(markRead) {
      if (this.notificationsOpen) {
        // make sure to mark notifs seen only when the notifs were open and not
        // from close-calls.
        this.notificationsOpen = false
        if (markRead && this.closingDrawerMarksAsSeen) {
          this.markNotificationsAsSeen()
        }
      }
    },
    notificationsTouchStart(e) {
      GestureService.beginSwipe(e, this.notificationsCloseGesture)
    },
    notificationsTouchMove(e) {
      GestureService.updateSwipe(e, this.notificationsCloseGesture)
    },
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    scrollMobileNotificationsToTop() {
      this.$refs.mobileNotifications.scrollTo(0, 0)
    },
    showConfirmLogout() {
      this.showingConfirmLogout = true
    },
    hideConfirmLogout() {
      this.showingConfirmLogout = false
    },
    logout() {
      if (!this.shouldConfirmLogout) {
        this.doLogout()
      } else {
        this.showConfirmLogout()
      }
    },
    doLogout() {
      useUsersStore().logout()
      this.hideConfirmLogout()
      this.$router.replace('/main/public')
    },
    markNotificationsAsSeen() {
      useNotificationsStore().markNotificationsAsSeen()
    },
    onScroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      this.notificationsAtTop = scrollTop > 0
    },
  },
  watch: {
    $route() {
      // handles closing notificaitons when you press any router-link on the
      // notifications.
      this.closeMobileNotifications()
    },
  },
}

export default MobileNav
